import React, { useMemo, useState } from 'react';
import { Empty, Segmented, Select } from 'antd';
import {
  CaretDownOutlined,
  CaretUpOutlined,
  DownOutlined,
  RightOutlined,
} from '@ant-design/icons';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { formatCurrency } from '../../../utils/currency';
import { formatLocalizedPercent, localeForLanguage } from '../../../utils/numberFormatting';
import {
  aggregateByDimension,
  aggregateMonthly,
  aggregateOrders,
  buildProductAnalysisOrders,
  comparisonMonth,
  metricValue,
  percentDelta,
  sortAnalysisGroups,
  type AnalysisComparison,
  type AnalysisDimension,
  type AnalysisGroup,
  type AnalysisGroupSort,
  type AnalysisGroupSortKey,
  type AnalysisMetric,
  type AnalysisOrder,
  type ProductAnalysisOrder,
} from './analysis';

interface Props {
  orders: AnalysisOrder[];
  totalOrders: number;
  metric: AnalysisMetric;
  dimension: AnalysisDimension;
  comparison: AnalysisComparison;
  onMetricChange: (metric: AnalysisMetric) => void;
  onDimensionChange: (dimension: AnalysisDimension) => void;
  onComparisonChange: (comparison: AnalysisComparison) => void;
}

function monthLabel(month: string, language: string): string {
  const [year, monthNumber] = month.split('-').map(Number);
  return new Intl.DateTimeFormat(localeForLanguage(language), {
    month: 'short',
    year: '2-digit',
    timeZone: 'UTC',
  }).format(new Date(Date.UTC(year, monthNumber - 1, 1)));
}

const ProfitabilityAnalysisWorkspace: React.FC<Props> = ({
  orders,
  totalOrders,
  metric,
  dimension,
  comparison,
  onMetricChange,
  onDimensionChange,
  onComparisonChange,
}) => {
  const { t, i18n } = useTranslation(['reports', 'orders', 'common']);
  const language = i18n.language || 'pl';

  const totals = useMemo(() => aggregateOrders(orders), [orders]);
  const months = useMemo(() => aggregateMonthly(orders), [orders]);
  const [expandedGroupKey, setExpandedGroupKey] = useState<string | null>(null);
  const [groupSort, setGroupSort] = useState<AnalysisGroupSort>({
    key: 'marginPercent',
    direction: 'asc',
  });
  const analysisRows = useMemo(() => (
    dimension === 'product' ? buildProductAnalysisOrders(orders) : orders
  ), [dimension, orders]);
  const groups = useMemo(() => sortAnalysisGroups(
    aggregateByDimension(analysisRows, dimension),
    groupSort,
  ), [analysisRows, dimension, groupSort]);
  const sourceRowsByGroup = useMemo(() => {
    const grouped = new Map<string, AnalysisOrder[]>();
    analysisRows.forEach((row) => {
      const key = dimension === 'customer'
        ? row.customer?.id || row.customer?.name || 'unknown'
        : (row as ProductAnalysisOrder).productKey;
      grouped.set(key, [...(grouped.get(key) || []), row]);
    });
    return grouped;
  }, [analysisRows, dimension]);

  const latestMonth = months[months.length - 1]?.month ?? null;
  const comparedMonth = latestMonth ? comparisonMonth(latestMonth, comparison) : null;
  const currentMonthAggregate = latestMonth ? months.find((month) => month.month === latestMonth) : undefined;
  const comparedMonthAggregate = comparedMonth ? months.find((month) => month.month === comparedMonth) : undefined;
  const currentMonthValue = currentMonthAggregate ? metricValue(currentMonthAggregate, metric) : 0;
  const comparisonDelta = percentDelta(
    currentMonthValue,
    comparedMonthAggregate ? metricValue(comparedMonthAggregate, metric) : undefined,
  );

  const chart = useMemo(() => {
    if (months.length === 0) return null;
    const width = 820;
    const height = 210;
    const padLeft = 68;
    const padRight = 18;
    const padTop = 18;
    const padBottom = 34;
    const plotWidth = width - padLeft - padRight;
    const plotHeight = height - padTop - padBottom;
    const values = months.map((month) => metricValue(month, metric));
    const min = Math.min(0, ...values);
    const max = Math.max(0, ...values);
    const span = max - min || 1;
    const x = (index: number) => padLeft + (months.length === 1 ? plotWidth / 2 : (index / (months.length - 1)) * plotWidth);
    const y = (value: number) => padTop + plotHeight - ((value - min) / span) * plotHeight;
    const points = values.map((value, index) => `${x(index)},${y(value)}`).join(' ');
    return { width, height, padLeft, padRight, padTop, padBottom, plotHeight, values, min, max, x, y, points };
  }, [metric, months]);

  const formatMetric = (value: number, selectedMetric: AnalysisMetric = metric) => (
    selectedMetric === 'marginPercent'
      ? formatLocalizedPercent(value, language)
      : formatCurrency(value, 'PLN')
  );

  const groupLabel = (group: AnalysisGroup) => {
    return group.label;
  };

  const deltaClass = (delta: number | null): string => {
    if (delta === null || delta === 0) return '';
    const isImprovement = metric === 'cost' ? delta < 0 : delta > 0;
    return isImprovement ? 'is-positive' : 'is-negative';
  };

  const changeGroupSort = (key: AnalysisGroupSortKey) => {
    setGroupSort((current) => {
      if (current.key === key) {
        return { ...current, direction: current.direction === 'asc' ? 'desc' : 'asc' };
      }
      return {
        key,
        direction: key === 'label' || key === 'marginPercent' ? 'asc' : 'desc',
      };
    });
  };

  const sortableHeader = (key: AnalysisGroupSortKey, label: string) => {
    const active = groupSort.key === key;
    const ariaSort = active
      ? (groupSort.direction === 'asc' ? 'ascending' : 'descending')
      : 'none';
    return (
      <th aria-sort={ariaSort}>
        <button
          type="button"
          className={active ? 'is-active' : ''}
          onClick={() => changeGroupSort(key)}
          aria-label={t('reports:profitability.analysis.sortBy', { column: label })}
        >
          <span>{label}</span>
          {active && (groupSort.direction === 'asc' ? <CaretUpOutlined /> : <CaretDownOutlined />)}
        </button>
      </th>
    );
  };

  return (
    <section className="profitability-analysis">
      <header className="profitability-analysis__header">
        <div>
          <span className="profitability-analysis__eyebrow">
            {t('reports:profitability.analysis.eyebrow')}
          </span>
          <h2>{t('reports:profitability.analysis.title')}</h2>
          <p>{t('reports:profitability.analysis.description')}</p>
        </div>
        <span className="profitability-analysis__scope">
          {t('reports:profitability.filters.showingCount', { shown: orders.length, total: totalOrders })}
        </span>
      </header>

      <div className="profitability-analysis-toolbar">
        <label>
          <span>{t('reports:profitability.analysis.metric')}</span>
          <Segmented<AnalysisMetric>
            value={metric}
            onChange={onMetricChange}
            options={[
              { value: 'margin', label: t('reports:profitability.analysis.metrics.margin') },
              { value: 'revenue', label: t('reports:profitability.analysis.metrics.revenue') },
              { value: 'cost', label: t('reports:profitability.analysis.metrics.cost') },
              { value: 'marginPercent', label: t('reports:profitability.analysis.metrics.marginPercent') },
            ]}
          />
        </label>
        <label>
          <span>{t('reports:profitability.analysis.perspective')}</span>
          <Segmented<AnalysisDimension>
            value={dimension}
            onChange={(value) => {
              setExpandedGroupKey(null);
              onDimensionChange(value);
            }}
            options={[
              { value: 'customer', label: t('reports:profitability.analysis.dimensions.customer') },
              { value: 'product', label: t('reports:profitability.analysis.dimensions.product') },
            ]}
          />
        </label>
        <label>
          <span>{t('reports:profitability.analysis.compareWith')}</span>
          <Select<AnalysisComparison>
            value={comparison}
            onChange={onComparisonChange}
            options={[
              { value: 'previousMonth', label: t('reports:profitability.analysis.comparisons.previousMonth') },
              { value: 'previousYear', label: t('reports:profitability.analysis.comparisons.previousYear') },
              { value: 'none', label: t('reports:profitability.analysis.comparisons.none') },
            ]}
          />
        </label>
      </div>

      <div className="profitability-analysis-summary">
        <div className="profitability-analysis-summary__primary">
          <span>{t(`reports:profitability.analysis.metrics.${metric}`)}</span>
          <strong>{formatMetric(metricValue(totals, metric))}</strong>
          <small>{t('reports:profitability.analysis.currentSlice')}</small>
        </div>
        <div>
          <span>{t('reports:profitability.analysis.orders')}</span>
          <strong>{totals.orderCount}</strong>
          <small>{t('reports:profitability.analysis.withCurrentFilters')}</small>
        </div>
        <div>
          <span>{t('reports:profitability.analysis.weightedMargin')}</span>
          <strong>{formatMetric(totals.marginPercent, 'marginPercent')}</strong>
          <small>{t('reports:profitability.analysis.weightedMarginHint')}</small>
        </div>
        <div className={deltaClass(comparisonDelta)}>
          <span>{t('reports:profitability.analysis.lastPeriodChange')}</span>
          <strong>{comparisonDelta === null ? '—' : formatLocalizedPercent(comparisonDelta, language)}</strong>
          <small>
            {latestMonth && comparedMonth
              ? t('reports:profitability.analysis.periodComparison', {
                current: monthLabel(latestMonth, language),
                previous: monthLabel(comparedMonth, language),
              })
              : t('reports:profitability.analysis.noComparison')}
          </small>
        </div>
      </div>

      <div className="profitability-analysis__main">
        <div className="profitability-analysis-trend">
          <div className="profitability-analysis-section-heading">
            <div>
              <h3>{t('reports:profitability.analysis.trendTitle')}</h3>
              <p>{t('reports:profitability.analysis.trendHint')}</p>
            </div>
            {latestMonth && <span>{monthLabel(latestMonth, language)}</span>}
          </div>
          {!chart ? (
            <Empty description={t('reports:profitability.analysis.noTrend')} image={Empty.PRESENTED_IMAGE_SIMPLE} />
          ) : (
            <svg
              viewBox={`0 0 ${chart.width} ${chart.height}`}
              role="img"
              aria-label={t('reports:profitability.analysis.trendTitle')}
            >
              {[0, 0.5, 1].map((fraction) => {
                const value = chart.min + (chart.max - chart.min) * fraction;
                return (
                  <g key={fraction}>
                    <line
                      x1={chart.padLeft}
                      x2={chart.width - chart.padRight}
                      y1={chart.y(value)}
                      y2={chart.y(value)}
                      className="profitability-analysis-trend__grid"
                    />
                    <text x={chart.padLeft - 10} y={chart.y(value) + 4} textAnchor="end">
                      {metric === 'marginPercent'
                        ? formatLocalizedPercent(value, language)
                        : new Intl.NumberFormat(localeForLanguage(language), { notation: 'compact' }).format(value)}
                    </text>
                  </g>
                );
              })}
              {chart.min < 0 && chart.max > 0 && (
                <line
                  x1={chart.padLeft}
                  x2={chart.width - chart.padRight}
                  y1={chart.y(0)}
                  y2={chart.y(0)}
                  className="profitability-analysis-trend__zero"
                />
              )}
              <polyline points={chart.points} className="profitability-analysis-trend__line" />
              {months.map((month, index) => (
                <g key={month.month}>
                  <circle
                    cx={chart.x(index)}
                    cy={chart.y(chart.values[index])}
                    r={4}
                    className="profitability-analysis-trend__point"
                  >
                    <title>{`${monthLabel(month.month, language)}: ${formatMetric(chart.values[index])}`}</title>
                  </circle>
                  <text x={chart.x(index)} y={chart.height - 8} textAnchor="middle" className="profitability-analysis-trend__month">
                    {monthLabel(month.month, language)}
                  </text>
                </g>
              ))}
            </svg>
          )}
        </div>

        <aside className="profitability-analysis-notes">
          <span>{t('reports:profitability.analysis.readingTitle')}</span>
          <strong>{t('reports:profitability.analysis.readingHeadline')}</strong>
          <p>{t('reports:profitability.analysis.readingBody')}</p>
        </aside>
      </div>

      <div className="profitability-analysis-ranking">
        <div className="profitability-analysis-section-heading">
          <div>
            <h3>{t('reports:profitability.analysis.breakdownTitle')}</h3>
            <p>{t('reports:profitability.analysis.breakdownHint', {
              perspective: t(`reports:profitability.analysis.dimensions.${dimension}`).toLocaleLowerCase(),
            })}</p>
          </div>
        </div>
        {groups.length === 0 ? (
          <Empty description={t('reports:profitability.analysis.noGroups')} image={Empty.PRESENTED_IMAGE_SIMPLE} />
        ) : (
          <div className="profitability-analysis-table-wrap">
            <table className="profitability-analysis-table">
              <thead>
                <tr>
                  {sortableHeader('label', t(`reports:profitability.analysis.dimensions.${dimension}`))}
                  {sortableHeader('orderCount', t('reports:profitability.analysis.orders'))}
                  {sortableHeader('revenue', t('reports:profitability.analysis.metrics.revenue'))}
                  {sortableHeader('cost', t('reports:profitability.analysis.metrics.cost'))}
                  {sortableHeader('marginPercent', t('reports:profitability.analysis.metrics.margin'))}
                </tr>
              </thead>
              <tbody>
                {groups.slice(0, 12).map((group) => {
                  const isExpanded = expandedGroupKey === group.key;
                  const sourceRows = sourceRowsByGroup.get(group.key) || [];
                  return (
                    <React.Fragment key={group.key}>
                      <tr className={isExpanded ? 'is-expanded' : ''}>
                        <td>
                          <button
                            type="button"
                            className="profitability-analysis-table__expand"
                            onClick={() => setExpandedGroupKey(isExpanded ? null : group.key)}
                            aria-expanded={isExpanded}
                          >
                            {isExpanded ? <DownOutlined /> : <RightOutlined />}
                            <strong>{groupLabel(group)}</strong>
                          </button>
                        </td>
                        <td>{group.orderCount}</td>
                        <td>{formatCurrency(group.revenue, 'PLN')}</td>
                        <td>{formatCurrency(group.cost, 'PLN')}</td>
                        <td className={group.margin < 0 ? 'is-negative' : 'is-positive'}>
                          {formatCurrency(group.margin, 'PLN')}
                          <small> ({formatLocalizedPercent(group.marginPercent, language)})</small>
                        </td>
                      </tr>
                      {isExpanded && (
                        <tr className="profitability-analysis-table__details">
                          <td colSpan={5}>
                            <div>
                              <span>{t('reports:profitability.analysis.sourceOrders')}</span>
                              {sourceRows.map((row) => {
                                const productRow = row as ProductAnalysisOrder;
                                const orderId = dimension === 'product' ? productRow.sourceOrderId : row.id;
                                const orderNumber = dimension === 'product'
                                  ? productRow.sourceOrderNumber
                                  : (row.useManualNumbering && row.manualOrderNumber ? row.manualOrderNumber : row.orderNumber || '—');
                                return (
                                  <Link key={row.id} to={`/app/orders/${orderId}`}>
                                    <strong>{orderNumber}</strong>
                                    <span>{row.customer?.name || '—'}</span>
                                    <b className={row.margin < 0 ? 'is-negative' : 'is-positive'}>
                                      {formatCurrency(row.margin, 'PLN')} ({formatLocalizedPercent(row.marginPercent, language)})
                                    </b>
                                  </Link>
                                );
                              })}
                            </div>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </section>
  );
};

export default ProfitabilityAnalysisWorkspace;
